<template>
  <div class="category" style="background-color: #fafafa;">
    <h1
      v-if="!loading"
      :class="{ 'category--loading': loading }"
      class="category__title">{{ currentTag.fields.title }}</h1>
    <div
      v-if="chunkArray.length"
      class="home-category-listings home-category-listings--category-page"
      style="background-color: transparent;">
      <TagCategoryList
        v-if="typeof chunkArray[0] !== 'undefined'"
        :list="chunkArray[0]" />
      <TagCategoryList
        v-if="typeof chunkArray[1] !== 'undefined'"
        :list="chunkArray[1]"
        :rellaxSpeed="2" />
      <TagCategoryList
        v-if="typeof chunkArray[2] !== 'undefined'"
        :list="chunkArray[2]" />
    </div>
    <div
      v-if="!loading"
      class="category_contents">
      <div v-html="description" />
    </div>
    <div
      v-if="!loading"
      v-next
      @next="$_goNext"
      class="category__next">
        &nbsp;
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { splitEvery, transpose } from 'ramda'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

import TagCategoryList from '@/components/TagCategoryList/TagCategoryList.vue'
import matchMedia from '@/mixins/matchMedia.js'

export default {
  name: 'Tag',
  mixins: [matchMedia],
  components: {
    TagCategoryList
  },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data: () => ({
    observerRef: undefined
  }),
  computed: {
    ...mapGetters('contentful', [
      'tags',
      'tag',
      'products'
    ]),
    ...mapGetters([
      'lang'
    ]),
    loading: {
      get () {
        return typeof this.currentTag === 'undefined'
      }
    },
    currentTag: {
      get () {
        return this.tag(this.slug)
      }
    },
    tagProducts: {
      get () {
        if (typeof this.currentTag === 'undefined') return []
        return this.products.reduce((accumulator, productElement) => {
          if (productElement.fields.tags === undefined) return accumulator
          productElement.fields.tags.forEach(tag => {
            if (tag.fields.slug === this.currentTag.fields.slug) return accumulator.push(productElement)
          })
          return accumulator
        }, [])
      }
    },
    chunkArray: {
      get () {
        if (this.loading) return [[], [], []]
        if (!this.tagProducts.length) return [[], [], []]
        if (this.matchMedia === 'medium' || this.matchMedia === 'small') {
          return transpose(splitEvery(2, this.tagProducts))
        }
        return transpose(splitEvery(3, this.tagProducts))
      }
    },
    description: {
      get () {
        if (this.loading) return ''
        if (typeof this.currentTag.fields.description === 'undefined') return ''
        return documentToHtmlString(this.currentTag.fields.description)
      }
    },
    nextTagSlug: {
      get () {
        if (this.loading) return ''
        // get the current category index in the categories array
        const currentIndex = this.tags.findIndex((element) => element === this.currentTag)
        // if next element exists, return its slug. If not, go back to the fist element
        if (typeof this.tags[currentIndex + 1] === 'undefined') return this.tags[0].fields.slug
        return this.tags[currentIndex + 1].fields.slug
      }
    }
  },
  beforeRouteEnter (from, to, next) {
    next(async (vm) => {
	    await vm.loadData()
      if (typeof vm.currentTag === 'undefined') vm.$router.replace({ name: `${vm.lang}.not-found-page` })
    })
  },
  beforeRouteUpdate (from, to, next) {
    this.loadData()
    next()
  },
  methods: {
    ...mapActions('contentful', [
      'loadProducts',
      'loadTags'
    ]),
    async loadData () {
      try {
        await Promise.all([this.loadTags(), this.loadProducts()])
      } catch (e) {
        // TODO sentry
      }
    },
    $_goNext () {
      if (this.loading) return
      this.$router.push({ name: `${this.lang}.single-tag`, params: { slug: this.nextTagSlug } })
    }
  },
  directives: {
    'next': {
      inserted (el, binding, vnode) {
        const observer = new window.IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) el.dispatchEvent(new Event('next'))
          })
        },
        {
          threshold: [0.9]
        })
        observer.observe(el)
        vnode.context.$data.observerRef = observer
      },
      unbind (el, binding, vnode) {
        vnode.context.$data.observerRef.unobserve(el)
      }
    }
  },
  metaInfo () {
    if (!this.loading) return {
      title: this.currentTag.fields.seoTitle,
      meta: [
        { vmid: 'description', name: 'description', content: this.currentTag.fields.seoDescription },
        { property: 'og:locale', content: this.lang },
        { property: 'og:title', content: this.currentTag.fields.seoTitle },
        { property: 'og:description', content: this.currentTag.fields.seoDescription },
        { property: 'og:url', content: `/${this.lang}/tag/${this.slug}` }
      ],
      link: [
        { rel: 'alternate', href: `${window.location.origin}/it/tag/${this.slug}`, hreflang: 'it' },
        { rel: 'alternate', href: `${window.location.origin}/en/tag/${this.slug}`, hreflang: 'en' },
        { rel: 'canonical', href: location.protocol + '//' + location.host + location.pathname }
      ]
    }
    return {
      title: 'Loading...',
      meta: [
        { vmid: 'description', name: 'description', content: 'Loading...' },
        { property: 'og:locale', content: 'Loading...' },
        { property: 'og:title', content: 'Loading...' },
        { property: 'og:description', content: 'Loading...' },
        { property: 'og:url', content: 'Loading...' }
      ],
      link: [
        { rel: 'alternate', href: 'Loading...', hreflang: 'it' },
        { rel: 'alternate', href: 'Loading...', hreflang: 'en' }
      ]
    }
  }
}
</script>

<style lang="scss">
  @import "./component.scss";
</style>
